import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, INR, INR2, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Coins, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MetalToggle from "@/components/MetalToggle";

const CHIPS = [500, 1000, 2500, 5000, 10000];

export default function DepositModal({ open, onOpenChange, onDone, defaultMetal = "gold" }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState(1000);
  const [metal, setMetal] = useState(defaultMetal);
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMetal(defaultMetal);
    api.get("/market/price").then(({ data }) => setPrices(data));
  }, [open, defaultMetal]);

  const price = metal === "gold" ? prices?.price_24k_inr_per_gram : prices?.price_silver_inr_per_gram;
  const grams = price ? Number(amount) / price : 0;

  const finishAndClose = async (order_id, payment_id, signature) => {
    try {
      const { data } = await api.post("/wallet/deposit/verify", {
        razorpay_order_id: order_id,
        razorpay_payment_id: payment_id,
        razorpay_signature: signature,
      });
      toast.success(`Purchased ${data.grams_added.toFixed(4)}g gold`);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data: order } = await api.post("/wallet/deposit/order", {
        amount_inr: Number(amount),
        metal,
      });

      // Mock mode: no Razorpay keys — simulate a payment and hit verify immediately
      if (order.mock) {
        await finishAndClose(order.order_id, "pay_mock_123", "mock_signature");
        return;
      }

      // Live mode: open Razorpay Checkout
      if (typeof window.Razorpay === "undefined") {
        toast.error("Payment SDK didn't load. Please refresh and try again.");
        return;
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount_paise,
        currency: "INR",
        name: "ShreeDhan",
        description: `Digital ${metal} purchase`,
        theme: { color: metal === "gold" ? "#C9922A" : "#B8B8B8" },
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone ? (user.phone.startsWith("+") ? user.phone : `+91${user.phone}`) : "",
        },
        notes: { flow: "buy_gold" },
        handler: async (response) => {
          await finishAndClose(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature,
          );
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="deposit-modal" className="max-w-md rounded-3xl bg-brand-sand border-brand-line">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-brand-cream flex items-center gap-2">
            <Coins className="text-brand-primary" size={20} /> Buy {metal === "gold" ? "Gold" : "Silver"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex justify-center">
          <MetalToggle value={metal} onChange={setMetal} testId="deposit-metal-toggle" />
        </div>

        <div className="rounded-2xl bg-brand-sand2 border border-brand-line-faint p-4">
          <div className="text-xs uppercase tracking-wider text-brand-slate">
            Live {metal === "gold" ? "24k" : "silver"} price
          </div>
          <div className="font-display text-2xl text-brand-cream tabular-nums">
            {INR2(price || 0)}
            <span className="text-sm text-brand-slate"> /gram</span>
          </div>
        </div>

        <div>
          <Label className="text-brand-cream text-sm font-semibold">Choose amount</Label>
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {CHIPS.map((c) => (
              <button
                key={c}
                data-testid={`deposit-chip-${c}`}
                onClick={() => setAmount(c)}
                className={`shrink-0 h-11 px-4 rounded-full text-sm font-semibold border transition-transform active:scale-95 ${
                  Number(amount) === c
                    ? "bg-brand-primary text-brand-page border-brand-primary"
                    : "bg-brand-sand2 text-brand-cream border-brand-line-faint"
                }`}
              >
                {INR(c)}
              </button>
            ))}
          </div>
          <Input
            data-testid="deposit-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-3 h-12 rounded-xl bg-brand-sand2 border-brand-line-faint tabular-nums text-brand-cream"
          />
        </div>

        <div className="rounded-2xl bg-brand-sand2 border border-brand-line text-brand-cream p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-brand-slate">You get</span>
            <span className="font-semibold tabular-nums text-brand-accent">{grams.toFixed(4)}g</span>
          </div>
          <div className="mt-1 flex items-center justify-between text-sm">
            <span className="text-brand-slate">Payment</span>
            <span className="font-semibold tabular-nums text-brand-cream">{INR(amount)}</span>
          </div>
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-brand-slate2">
            <ShieldCheck size={12} /> Powered by Razorpay · UPI/Cards/Netbanking
          </div>
        </div>

        <Button
          data-testid="deposit-submit"
          onClick={submit}
          disabled={loading || !amount || amount <= 0}
          className="w-full h-14 rounded-full bg-gold-gradient text-brand-page font-semibold text-base shadow-gold"
        >
          {loading ? "Processing…" : `Pay ${INR(amount)}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
